/**
 * Every audit event the backend records, in words a person reads.
 *
 * NEW-R12-ACTIVITY-RAW-EVENT (grader round 12): the trust overview's Recent
 * activity printed "admin setup.committed" and "user trust.created", the role
 * slug and the event code as stored. The dashboard feed went through
 * `labelForEvent`, but that only knew 50 of the backend's 200-odd events and
 * turned the rest into a capitalised code ("Setup committed").
 *
 * So this is the ONE map, and it covers every action the backend writes:
 * `__tests__/event-labels.test.ts` reads the backend source and fails when an
 * emitted action has no entry here. An action this map has never heard of (a
 * newer backend, a retired event in an old record) reads as a neutral
 * "Activity recorded", never as its code. The audit log screens keep the code
 * on a tooltip for the record.
 *
 * Wording: sentence case, the thing first and what happened to it after
 * ("Trust opened"), so it reads after the actor's role ("Administrator ·
 * Trust opened") and on its own in a list.
 */

export const EVENT_LABELS: Record<string, string> = {
  // Resolutions. Each status change has its own event (grader round 12,
  // browser pass 3): a resolution created as a draft is "drafted"; one issued
  // to the trustees when it is created ("Issue to trustees") is "issued for
  // signature"; a draft later sent out is "sent for signature"; one pulled back
  // is "taken back to draft"; the last signature adds "completed".
  'resolution.created': 'Resolution drafted',
  'resolution.issued': 'Resolution issued for signature',
  'resolution.circulated': 'Resolution sent for signature',
  'resolution.recalled': 'Resolution taken back to draft',
  'resolution.signed': 'Resolution signed',
  'resolution.completed': 'Resolution completed, all signatures in',
  'resolution.cancelled': 'Resolution cancelled',
  'resolution.deleted': 'Resolution deleted',
  'resolution.updated': 'Resolution edited',
  'resolution.body.updated': 'Resolution body edited',
  'resolution.imported': 'Signed resolution imported',
  'resolution.external-signed': 'Resolution signed by an outside signer',
  'resolution.external-signed-declined': 'Outside signer declined a resolution',
  'resolution.external-signer-invited': 'Outside signer invited to sign',
  'external-sign.otp.requested': 'Signing code requested',
  'external-sign.otp.verified': 'Signing code confirmed',

  // Documents
  'document.uploaded': 'Document uploaded',
  'document.viewed': 'Document viewed',
  'document.scanned': 'Document scan completed',
  'document.rescanned': 'Document re-scanned',
  'document.deleted': 'Document removed',
  'document.archived': 'Document archived',
  'document.unarchived': 'Document restored from the archive',
  'document.finalized': 'Document finalised',
  'document.reassigned': 'Document moved to another trust',
  'document.retyped': 'Document type changed',
  'document.download.issued': 'Document downloaded',
  'document.duplicate-skipped': 'Duplicate document skipped',
  'document.ingestion.started': 'Document reading started',
  'document.ingestion.confirmed': 'Document reading confirmed',
  'ingestion.job.redriven': 'Document reading restarted',
  'admin.document.rescan-requested': 'Document re-scan requested',
  'letter-of-authority.appended': 'Letter of Authority added',
  'letter-of-wishes.appended': 'Letter of wishes added',
  'letter-of-wishes.acknowledged': 'Letter of wishes acknowledged',

  // Trust lifecycle and setup
  'trust.created': 'Trust opened',
  'trust.updated': 'Trust details updated',
  'trust.deleted': 'Trust deleted',
  'trust.deed.updated': 'Trust deed updated',
  'trust.deactivated': 'Trust closed',
  'setup.committed': 'Trust structure set up from the documents',
  'register.backfilled': 'Register filled in from the documents',
  'trust.access-requested': 'Access to the trust requested',
  'trust.access-approved': 'Access to the trust approved',
  'trust.access-rejected': 'Access to the trust declined',
  'trust.member.revoked': 'Portal access removed',
  'trust.member.tier-changed': 'Portal access level changed',
  'trust.member.verified-for-acting': 'Person verified to act for the trust',
  'trust.allocation.targets.set': 'Allocation targets set',
  'trust.company.added': 'Company added to the trust',
  'trust.property.added': 'Property added to the trust',
  'trust.jse-holding.added': 'Listed share holding added',
  'trust.holding.updated': 'Holding updated',
  'trust.holding.revalued': 'Holding revalued',
  'trust.holding.deleted': 'Holding removed',
  'trust.revaluation.completed': 'Portfolio revaluation completed',

  // Assets and companies
  'asset.created': 'Asset recorded',
  'asset.updated': 'Asset updated',
  'asset.confirmed': 'Asset confirmed',
  'asset.reassigned': 'Asset moved to another owner',
  'asset.duplicate-skipped': 'Duplicate asset skipped',
  'asset.evidence-attached': 'Evidence attached to an asset',
  'asset.evidence-detached': 'Evidence removed from an asset',
  'asset.notice-sent': 'Asset notice sent',
  'asset.notice-failed': 'Asset notice not delivered',
  'company.created': 'Company recorded',
  'company.updated': 'Company details updated',

  // Beneficial ownership
  'bo.lodged': 'Beneficial-ownership lodgement filed',
  'bo.amended': 'Beneficial-ownership record amended',
  'bo.attested': 'Beneficial-ownership attested',
  'bo.register-exported': 'Beneficial-ownership register exported',
  'beneficial-ownership.created': 'Beneficial owner added',
  'beneficial-ownership.updated': 'Beneficial owner updated',
  'beneficial-ownership.deleted': 'Beneficial owner removed',
  'beneficial-ownership.fica-check.recorded': 'FICA check recorded',
  'beneficial-ownership.fica-check.withdrawn': 'FICA check withdrawn',
  'beneficial-ownership.fica-check.invalidated': 'FICA check no longer current',
  'beneficial-ownership.lodge-master.lodged': 'Register lodged with the Master',
  'beneficial-ownership.lodge-master.success': 'Register lodged with the Master',
  'beneficial-ownership.lodge-master.rejected': 'Register lodgement rejected',
  'beneficial-ownership.lodge-master.screening-deferred': 'Register lodged before screening',
  'master-lodgement.advanced': 'Master lodgement moved on a step',
  'master-lodgement.rejected': 'Master lodgement rejected',

  // Beneficiaries and trustees
  'beneficiaries.added': 'Beneficiaries added',
  'beneficiary.created': 'Beneficiary added',
  'beneficiary.updated': 'Beneficiary updated',
  'beneficiary.notified': 'Beneficiary notified',
  'beneficiary.notice.failed': 'Beneficiary notice not delivered',
  'beneficiary.lifecycle.deceased.recorded': 'Beneficiary passing recorded',
  'beneficiary.lifecycle.deceased.reverted': 'Beneficiary passing record withdrawn',
  'trustees.appointed': 'Trustee appointed',
  'trustees.resigned': 'Trustee resigned',
  'trustees.invited': 'Trustees invited',

  // Distributions and tax
  'distribution.drafted': 'Distribution drafted',
  'distribution.created': 'Distribution drafted',
  'distribution.authorised': 'Distribution authorised',
  'distribution.released': 'Distribution released',
  'distribution.cancelled': 'Distribution cancelled',
  'distribution.voted': 'Vote cast on a distribution',
  'distribution.acknowledged': 'Distribution acknowledged',
  'distribution.resolution.linked': 'Resolution linked to a distribution',
  'distribution.payout.initiated': 'Distribution payment started',
  'distribution.payout.failed': 'Distribution payment failed',
  'tax-certificate.created': 'Tax certificate drafted',
  'tax-certificate.issued': 'Tax certificate issued',
  'tax-certificate.amended': 'Tax certificate amended',

  // FICA, compliance and reports
  'fica.completed': 'FICA declarations recorded',
  'fica.redeclared': 'FICA declarations renewed',
  'fica.pep-escalation-failed': 'Politically exposed person alert not delivered',
  'fica.report.drafted': 'FIC report drafted',
  'fica.report.submitted': 'FIC report submitted',
  'fica.risk.rated': 'Risk rating updated',
  'risk-rating.updated': 'Risk rating updated',
  'fica-retention.expired.deleted': 'Expired FICA records deleted',
  'fic-report.drafted': 'FIC report drafted',
  'fic-report.reviewed': 'FIC report reviewed',
  'fic-report.review-withdrawn': 'FIC report review withdrawn',
  'fic-report.submitted': 'FIC report submitted',
  'fic-report.outcome-recorded': 'FIC report outcome recorded',
  'fic-report.goaml-push-failed': 'FIC report not delivered to goAML',
  'edd-case.opened': 'Enhanced due diligence case opened',
  'edd-case.captured': 'Enhanced due diligence details captured',
  'edd-case.decided': 'Enhanced due diligence decision recorded',
  'edd-case.adjudicated': 'Enhanced due diligence case reviewed',
  'firm-compliance-report.drafted': 'Compliance report drafted',
  'firm-compliance-report.updated': 'Compliance report updated',
  'identity.verified': 'Identity verified',

  // Privacy (POPIA) and legal holds
  'dsar.requested': 'Data request received',
  'dsar.acknowledged': 'Data request acknowledged',
  'dsar.fulfilled': 'Data request fulfilled',
  'dsar.declined': 'Data request declined',
  'dsar.erase.approval.recorded': 'Erasure approval recorded',
  'admin.dsar.updated': 'Data request updated',
  'admin.dsar.export.generated': 'Data request export prepared',
  'erasure.request.executed': 'Erasure carried out',
  'consent.cookie.updated': 'Cookie choices updated',
  'legal-hold.created': 'Legal hold opened',
  'legal-hold.released': 'Legal hold released',
  'legal-hold.cleared': 'Legal hold released',

  // Meetings and messages
  'meeting.scheduled': 'Meeting scheduled',
  'meeting.created': 'Meeting scheduled',
  'meeting.agenda_recorded': 'Meeting agenda recorded',
  'meeting.finalised': 'Meeting minutes finalised',
  'inbox.thread.created': 'Conversation started',
  'inbox.thread.read': 'Conversation read',
  'inbox.thread.archived': 'Conversation archived',
  'inbox.thread.participant-added': 'Person added to a conversation',
  'inbox.message.posted': 'Message sent',
  'inbox.message.redacted': 'Message redacted',
  'message.read': 'Message read',

  // People, accounts and sign-in
  'user.created': 'Account created',
  'user.updated': 'Account updated',
  'user.profile.updated': 'Profile updated',
  'user.notification-settings.updated': 'Notification settings updated',
  'user.lifecycle.death.recorded': 'Passing recorded',
  'user.lifecycle.incapacity.recorded': 'Incapacity recorded',
  'users.lifecycle.death': 'Trustee passing recorded',
  'users.lifecycle.incapacity': 'Trustee incapacity recorded',
  'auth.mfa.enrolled': 'Second factor enrolled',
  'auth.mfa.reset': 'Second factor reset',
  'auth.mfa.reset.executed': 'Second factor reset',
  'auth.mfa_factors_reset': 'Second factor reset',
  'auth.backup-code.verified': 'Backup code used to sign in',
  'auth.backup-codes.issued': 'Backup codes issued',
  'auth.backup-codes.regenerated': 'Backup codes replaced',
  'auth.session.revoked': 'Session revoked',
  'auth.sessions.revoked-others': 'Other sessions signed out',
  'auth.recovery.requested': 'Account recovery requested',
  'auth.recovery.executed': 'Account recovered',
  'auth.password-reset.forced': 'Password reset required',
  'auth.step-up.refreshed': 'Identity re-confirmed',
  'auth.step-up.webauthn': 'Identity re-confirmed with a passkey',
  'auth.webauthn.enrolled': 'Passkey added',
  'auth.webauthn.removed': 'Passkey removed',
  'auth.webauthn.clone-detected': 'Passkey warning raised',
  'auth.jwks.kid.revoked': 'Signing key withdrawn',
  'push.token.registered': 'Device registered for notifications',
  'welcome-email.abandoned': 'Welcome email not delivered',

  // Firm, team and access
  'client.onboarded': 'Client onboarded',
  'client.self_joined': 'Client joined',
  'firm.client.onboarded': 'Client onboarded by the firm',
  'firm.client.identity-recorded': 'Client identity recorded by the firm',
  'firm.director.recorded': 'Firm director recorded',
  'firm.act-as.started': 'Firm started acting for the client',
  'firm-client.assignees-set': 'Client team assigned',
  'firm-client.payer-set': 'Client billing payer set',
  'firm-grant.created': 'Firm given access',
  'firm-grant.revoked': 'Firm access removed',
  'firm-grant.resigned': 'Firm stepped down',
  'firm-grant.visibility-changed': 'Firm access changed',
  'team.invite.created': 'Team member invited',
  'team.invite.reissued': 'Team invitation sent again',
  'team.invite.accepted': 'Team invitation accepted',
  'team.member.removed': 'Team member removed',
  'membership.invite.accepted': 'Workspace invitation accepted',
  'membership.invite.declined': 'Workspace invitation declined',
  'external-auditor.granted': 'External auditor given access',
  'external-auditor.revoked': 'External auditor access removed',
  'external-auditor.expired': 'External auditor access ended',
  'admin.team.invite-sent': 'Team invitation sent',
  'admin.user.sessions.revoked': 'Team member signed out everywhere',
  'admin.user.mlro-designation.updated': 'Money laundering reporting officer changed',
  'admin.io.designated': 'Information officer designated',
  'admin.io.transitioned': 'Information officer changed',
  'admin.deputy-io.designated': 'Deputy information officer designated',
  'admin.recovery-request.approved': 'Account recovery approved',
  'admin.recovery-request.rejected': 'Account recovery declined',
  'admin.recovery-request.reissued': 'Account recovery link sent again',
  'admin.branding.updated': 'Branding updated',
  'admin.custom_roles.updated': 'Custom roles updated',
  'admin.role_labels.updated': 'Role titles updated',
  'admin.open_signup.updated': 'Sign-up setting changed',
  'admin.require_verified_before_acting.updated': 'Verification rule for acting changed',
  'integration.connected': 'Integration connected',
  'integration.disconnected': 'Integration disconnected',

  // Workspaces (tenants)
  'tenant.created': 'Trust workspace opened',
  'tenant.updated': 'Workspace updated',
  'tenant.suspended': 'Workspace suspended',
  'tenant.reactivated': 'Workspace reactivated',
  'tenant.delete.requested': 'Workspace deletion requested',
  'tenant.delete.approved': 'Workspace deletion approved',
  'tenant.deleted': 'Workspace deleted',
  'tenant.erasure.executed': 'Workspace erased',
  'superadmin.impersonate.started': 'Support session started',
  'superadmin.impersonate.stopped': 'Support session ended',

  // Billing
  'billing.charge-success': 'Payment received',
  'billing.deferred': 'Billing deferred',
  'billing.dunning.entered': 'Payment overdue',
  'billing.dunning.cleared': 'Overdue payment settled',
  'billing.dunning.clear_unrecorded': 'Overdue payment settled',
  'billing.dunning.suspended': 'Account paused for an overdue payment',
  'billing.override.applied': 'Billing adjusted',
  'billing.payment-method.transaction-initialized': 'Card details requested',
  'billing.refund.issued': 'Refund issued',
  'billing.subscription.created': 'Subscription started',
  'billing.subscription.selected': 'Plan chosen',
  'billing.subscription.updated': 'Subscription changed',

  // System messages from outside services
  'webhook.paystack.processed': 'Payment update received',
  'webhook.kyc.thisisme.processed': 'Identity check result received',
  'webhook.aes.impression.processed': 'Signing update received',
  'webhook.aes.trustfactory.processed': 'Signing update received',
  'webhook.supabase.processed': 'Sign-in service update received',
};

/**
 * Actions the backend builds from a template. `*` stands for one variable
 * part, and the pattern is written exactly as the backend's template with
 * each `${...}` replaced by `*` (the coverage test compares them that way).
 */
export const EVENT_PATTERNS: ReadonlyArray<readonly [pattern: string, label: string]> = [
  ['dsar.*.requested', 'Data request received'],
  ['admin.user.lifecycle.*.executed', 'Life event recorded for a team member'],
  ['billing.*', 'Billing update recorded'],
];

/** The neutral line for an action this map does not know. Never the code. */
export const UNKNOWN_EVENT_LABEL = 'Activity recorded';

/** A star in the middle stands for one dotted segment; a trailing star for the rest. */
function patternMatches(pattern: string, action: string): boolean {
  const parts = pattern.split('*').map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  let body = '';
  parts.forEach((part, i) => {
    body += part;
    if (i === parts.length - 1) return;
    const trailing = i === parts.length - 2 && parts[i + 1] === '';
    body += trailing ? '.+' : '[^.]+';
  });
  return new RegExp(`^${body}$`).test(action);
}

/** An audit event's action, as words. */
export function labelForEvent(action: string | undefined | null): string {
  if (!action) return UNKNOWN_EVENT_LABEL;
  const direct = EVENT_LABELS[action];
  if (direct) return direct;
  for (const [pattern, label] of EVENT_PATTERNS) {
    if (patternMatches(pattern, action)) return label;
  }
  return UNKNOWN_EVENT_LABEL;
}

/** The kind of thing an event is about, as a short noun for the row's tag. */
export const EVENT_SUBJECT_LABELS: Record<string, string> = {
  trust: 'Trust',
  document: 'Document',
  resolution: 'Resolution',
  asset: 'Asset',
  company: 'Company',
  beneficiary: 'Beneficiary',
  'beneficial-ownership': 'Ownership',
  bo: 'Ownership',
  distribution: 'Distribution',
  meeting: 'Meeting',
  user: 'Person',
  tenant: 'Workspace',
  member: 'Access',
  'trust-member': 'Access',
  'fic-report': 'FIC report',
  'edd-case': 'Due diligence',
  'tax-certificate': 'Tax',
  'legal-hold': 'Legal hold',
  dsar: 'Data request',
  thread: 'Message',
  message: 'Message',
  billing: 'Billing',
  subscription: 'Billing',
  holding: 'Holding',
};

/** The row tag for an event's subject; empty (no tag) when unknown. */
export function labelForEventSubject(subjectType: string | undefined | null): string {
  if (!subjectType) return '';
  return EVENT_SUBJECT_LABELS[subjectType] ?? '';
}
